import { getTimestampFormat, Sensor, SensorType } from 'common';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XStack } from 'tamagui';

import { AirFilterStatusContent } from './components/AirFilterStatusContent';
import { AirQualityStatusCard } from './components/AirQualityStatusCard';
import { HVACFilterAlertContent } from './components/HVACFilterAlertContent';
import { InHomeAirQualityContent } from './components/InHomeAirQualityContent';
import { InternalHumidityContent } from './components/InternalHumidityContent';
import { OutdoorAirQualityContent } from './components/OutdoorAirQualityContent';
import { ThermostatContent } from './components/ThermostatContent';
import { WidgetsWrapper } from '../../components/common';
import {
  DraggableItemData,
  DraggableWidgetCard,
} from '../../components/common/DraggableWidgetCard';
import { DraggableWrapper } from '../../components/common/DraggableWrapper';
import { useAppSync } from '../../contexts/AppSyncContext';
import { useHVACFilterAlert, useWidgetOrder, useHVACFilterAlertVisibility } from '../../hooks';
import { useExternalAirQualityData } from '../../hooks/air/useExternalAirQualityData';
import { useLatestSensorEvents } from '../../hooks/sensors/useLatestSensorEvent';

enum AirWidgetCardTypes {
  AirFilterStatus = 'AirFilterStatus',
  HVACFilterAlert = 'HVACFilterAlert',
  HVACFilterStatus = 'HVACFilterStatus',
  InHomeAirQuality = 'InHomeAirQuality',
  OutdoorAirQuality = 'OutdoorAirQuality',
  Thermostat = 'Thermostat',
  InternalHumidity = 'InternalHumidity',
  PM25Status = 'PM25Status',
  IAQStatus = 'IAQStatus',
}

const renderContent = (
  type: AirWidgetCardTypes,
  indoorSensor?: Sensor,
  correctedStatus?: string,
) => {
  switch (type) {
    case AirWidgetCardTypes.AirFilterStatus:
      return (
        <AirFilterStatusContent key={AirWidgetCardTypes.AirFilterStatus} sensor={indoorSensor} />
      );
    case AirWidgetCardTypes.HVACFilterAlert:
      return (
        <HVACFilterAlertContent
          key={AirWidgetCardTypes.HVACFilterAlert}
          sensor={indoorSensor}
          correctedStatus={correctedStatus}
        />
      );
    case AirWidgetCardTypes.InHomeAirQuality:
      return (
        <InHomeAirQualityContent key={AirWidgetCardTypes.InHomeAirQuality} sensor={indoorSensor} />
      );
    case AirWidgetCardTypes.OutdoorAirQuality:
      return <OutdoorAirQualityContent key={AirWidgetCardTypes.OutdoorAirQuality} />;
    case AirWidgetCardTypes.Thermostat:
      return <ThermostatContent key={AirWidgetCardTypes.Thermostat} sensor={indoorSensor} />;
    case AirWidgetCardTypes.InternalHumidity:
      return <InternalHumidityContent key={AirWidgetCardTypes.InternalHumidity} />;
    case AirWidgetCardTypes.PM25Status:
      return <AirQualityStatusCard key={AirWidgetCardTypes.PM25Status} type="pm25" />;
    case AirWidgetCardTypes.IAQStatus:
      return <AirQualityStatusCard key={AirWidgetCardTypes.IAQStatus} type="iaq" />;
  }
};

export const useAirQualityTimestampData = () => {
  const { event: airQualityEvent } = useLatestSensorEvents(SensorType.AIR_QUALITY);
  const { event: hvacEvent } = useLatestSensorEvents(SensorType.HVAC);
  const { qualityData: outdoorQualityData } = useExternalAirQualityData();
  const [airQualityLastEventTime, setAirQualityLastEventTime] = useState<number | undefined>();
  const [hvacLastEventTime, setHvacLastEventTime] = useState<number | undefined>();
  const [outdoorQualityLastEventTime, setOutdoorQualityLastEventTime] = useState<
    string | undefined
  >();

  useEffect(() => {
    if (airQualityEvent?.timestamp) {
      setAirQualityLastEventTime(airQualityEvent.timestamp * 1000);
    }
    if (hvacEvent?.timestamp) {
      setHvacLastEventTime(hvacEvent.timestamp * 1000);
    }
    if (outdoorQualityData?.timestamp) {
      setOutdoorQualityLastEventTime(outdoorQualityData.timestamp);
    }
  }, [airQualityEvent, hvacEvent, outdoorQualityData]);

  return {
    airFilterTimestamp: getTimestampFormat(hvacLastEventTime),
    inHomeAirQualityTimestamp: getTimestampFormat(airQualityLastEventTime),
    outdoorAirQualityTimestamp: outdoorQualityLastEventTime,
    thermostatTimestamp: getTimestampFormat(airQualityLastEventTime), // it uses the same sensor as inHomeAirQuality
    internalHumidityTimestamp: getTimestampFormat(airQualityLastEventTime), // it uses the same sensor as inHomeAirQuality
  };
};

export const Air: React.FC = () => {
  const { t } = useTranslation();
  const {
    airFilterTimestamp,
    inHomeAirQualityTimestamp,
    outdoorAirQualityTimestamp,
    thermostatTimestamp,
    internalHumidityTimestamp,
  } = useAirQualityTimestampData();

  const { sensors } = useAppSync();
  const indoorHvacSensors = useMemo(
    () => sensors.filter((s) => s.type === SensorType.HVAC_AIRHANDLER_MONITOR),
    [sensors],
  );
  const indoorAirQualitySensors = useMemo(
    () => sensors.filter((s) => s.type === SensorType.AIR_QUALITY),
    [sensors],
  );

  const hvacStatuses = indoorHvacSensors.map((s) => {
    const { filterReplacementStatus: f } = useHVACFilterAlert(s);
    const shouldShowAlert = useHVACFilterAlertVisibility(s);

    const correctedStatus = shouldShowAlert && f === 'NoFilterChangeNeeded' ? 'HVACFilterAlert' : f;

    return { sensorId: s.id, sensor: s, f: correctedStatus, shouldShowAlert };
  });

  // Create a stable key for alert states to trigger re-computation
  const alertStatesKey = useMemo(
    () =>
      hvacStatuses
        .map((status) => `${status.sensorId}:${status.shouldShowAlert}:${status.f}`)
        .join('|'),
    [hvacStatuses.map((s) => s.shouldShowAlert).join(''), hvacStatuses.map((s) => s.f).join('')],
  );

  const data: DraggableItemData<AirWidgetCardTypes>[] = useMemo(() => {
    const allCards: DraggableItemData<AirWidgetCardTypes>[] = [];

    // Add HVAC filter ALERT cards only when alerts are active (shouldShowAlert = true)
    hvacStatuses.forEach(
      ({ sensor, f: filterReplacementStatus, shouldShowAlert, correctedStatus }) => {
        console.log('WEB DEBUG Card Creation:', {
          sensorId: sensor.id,
          shouldShowAlert,
          filterReplacementStatus,
          correctedStatus,
        });

        if (shouldShowAlert) {
          console.log('WEB DEBUG: Creating ALERT card for sensor', sensor.id);
          // Only show alert card when there are active alerts - the card will handle different states
          allCards.push({
            id: AirWidgetCardTypes.HVACFilterAlert + sensor.id,
            title: t('hvac.filterAlert'),
            type: AirWidgetCardTypes.HVACFilterAlert,
            object: sensor,
            // Store corrected status in the card data
            correctedStatus,
          });
        }
      },
    );

    // Second: Add AirFilterStatus cards
    indoorHvacSensors.forEach((sensor) => {
      allCards.push({
        id: AirWidgetCardTypes.AirFilterStatus + sensor.id,
        title: t('air.filterStatus') + ' - ' + sensor.location,
        type: AirWidgetCardTypes.AirFilterStatus,
        object: sensor,
      });
    });

    // Third: Add Air Quality cards
    indoorAirQualitySensors.forEach((sensor) => {
      allCards.push({
        id: AirWidgetCardTypes.InHomeAirQuality + sensor.id,
        title: t('air.iaqOverWeek') + ' - ' + sensor.location,
        type: AirWidgetCardTypes.InHomeAirQuality,
        object: sensor,
      });
      allCards.push({
        id: AirWidgetCardTypes.PM25Status + sensor.id,
        title: t('air.inHomeAirQuality'),
        type: AirWidgetCardTypes.PM25Status,
        object: sensor,
      });
      allCards.push({
        id: AirWidgetCardTypes.IAQStatus + sensor.id,
        title: t('air.inHomeAirQuality'),
        type: AirWidgetCardTypes.IAQStatus,
        object: sensor,
      });
      // Fourth: Add Thermostat cards
      allCards.push({
        id: AirWidgetCardTypes.Thermostat + sensor.id,
        title: t('air.thermostat') + ' - ' + sensor.location,
        type: AirWidgetCardTypes.Thermostat,
        object: sensor,
      });
      // Fifth: Add Humidity cards
      allCards.push({
        id: AirWidgetCardTypes.InternalHumidity + sensor.id,
        title: t('air.humidity', { location: sensor.location }),
        type: AirWidgetCardTypes.InternalHumidity,
        object: sensor,
      });
    });

    // Last: Add Outdoor Air Quality card
    allCards.push({
      id: AirWidgetCardTypes.OutdoorAirQuality,
      title: t('air.outdoorAirQuality'),
      type: AirWidgetCardTypes.OutdoorAirQuality,
    });

    return allCards;
  }, [sensors, indoorHvacSensors, indoorAirQualitySensors, t, alertStatesKey]);

  const getTimestampForWidgetType = (type: AirWidgetCardTypes): string | undefined => {
    switch (type) {
      case AirWidgetCardTypes.AirFilterStatus:
        return airFilterTimestamp;
      case AirWidgetCardTypes.InHomeAirQuality:
        return inHomeAirQualityTimestamp;
      case AirWidgetCardTypes.OutdoorAirQuality:
        return outdoorAirQualityTimestamp;
      case AirWidgetCardTypes.Thermostat:
        return thermostatTimestamp;
      case AirWidgetCardTypes.InternalHumidity:
        return internalHumidityTimestamp;
      case AirWidgetCardTypes.PM25Status:
        return inHomeAirQualityTimestamp;
      case AirWidgetCardTypes.IAQStatus:
        return inHomeAirQualityTimestamp;
      default:
        return undefined;
    }
  };

  const {
    items,
    handleItemsReordered,
    handleStartDragging,
    handleSaveOrder,
    handleCancelOrder,
    isItemEnabled,
    setItemEnabled,
  } = useWidgetOrder<DraggableItemData<AirWidgetCardTypes>>('airWidgetsOrder', data);

  return (
    <WidgetsWrapper
      onStartDragging={handleStartDragging}
      onSaveOrder={handleSaveOrder}
      onCancelOrder={handleCancelOrder}>
      <XStack flex={1}>
        <DraggableWrapper items={items} onItemsReordered={handleItemsReordered}>
          {items.map((item, index) => (
            <DraggableWidgetCard
              key={item.id}
              item={item}
              index={index}
              enabled={isItemEnabled(item.id)}
              timestamp={item.type ? getTimestampForWidgetType(item.type) : undefined}
              error={false}
              title={item.title}
              onEnabledChange={(enabled) => setItemEnabled(item.id, enabled)}>
              {item.type && renderContent(item.type, item.object, (item as any).correctedStatus)}
            </DraggableWidgetCard>
          ))}
        </DraggableWrapper>
      </XStack>
    </WidgetsWrapper>
  );
};
